/**
 * FartInventory - Manages player's fart ammo and selection
 * 
 * Tracks ammo for each fart type and handles selection cycling.
 */

import { FartType, FART_CONFIGS, getFartConfig } from './FartTypes';

export interface FartAmmo {
    type: FartType;
    count: number;
}

export class FartInventory {
    private ammo: Map<FartType, number> = new Map();
    private selectedType: FartType = FartType.BASIC;
    private availableTypes: FartType[] = [FartType.BASIC];

    constructor() {
        // Initialize all special fart types with 0 ammo
        for (const type of Object.values(FartType)) {
            if (type !== FartType.BASIC) {
                this.ammo.set(type, 0);
            }
        }
    }

    /**
     * Get currently selected fart type
     */
    getSelectedType(): FartType {
        return this.selectedType;
    }

    /**
     * Get ammo count for a fart type
     */
    getAmmo(type: FartType): number {
        if (type === FartType.BASIC) return Infinity;
        return this.ammo.get(type) ?? 0;
    }

    /**
     * Add ammo for a fart type (from pickup)
     */
    addAmmo(type: FartType, amount: number): void {
        if (type === FartType.BASIC) return; // Basic is unlimited

        const current = this.ammo.get(type) ?? 0;
        this.ammo.set(type, current + amount);
        this.updateAvailableTypes();
    }

    /**
     * Use one ammo of the currently selected type
     * Returns true if successful, false if no ammo
     */
    useAmmo(): boolean {
        if (this.selectedType === FartType.BASIC) return true;

        const current = this.ammo.get(this.selectedType) ?? 0;
        if (current <= 0) {
            // Auto-switch to basic if out of ammo
            this.selectedType = FartType.BASIC;
            this.updateAvailableTypes();
            return false;
        }

        this.ammo.set(this.selectedType, current - 1);

        // Auto-switch to basic if we just used the last one
        if (current - 1 <= 0) {
            this.selectedType = FartType.BASIC;
        }

        this.updateAvailableTypes();
        return true;
    }

    /**
     * Check if we can fire the currently selected fart
     */
    canFire(): boolean {
        if (this.selectedType === FartType.BASIC) return true;
        return (this.ammo.get(this.selectedType) ?? 0) > 0;
    }

    /**
     * Select next available fart type (swipe up)
     */
    selectNext(): FartType {
        const currentIndex = this.availableTypes.indexOf(this.selectedType);
        const nextIndex = (currentIndex + 1) % this.availableTypes.length;
        this.selectedType = this.availableTypes[nextIndex];
        return this.selectedType;
    }

    /**
     * Select previous available fart type (swipe down)
     */
    selectPrevious(): FartType {
        const currentIndex = this.availableTypes.indexOf(this.selectedType);
        const prevIndex = (currentIndex - 1 + this.availableTypes.length) % this.availableTypes.length;
        this.selectedType = this.availableTypes[prevIndex];
        return this.selectedType;
    }

    /**
     * Directly select a fart type (keyboard number keys)
     * Falls back to Basic if trying to select an empty special fart
     */
    selectType(type: FartType): boolean {
        if (type === FartType.BASIC) {
            this.selectedType = FartType.BASIC;
            return true;
        }
        if (this.getAmmo(type) > 0) {
            this.selectedType = type;
            return true;
        }
        // Can't select empty special fart, stay on current or fall back to Basic
        return false;
    }

    /**
     * Get all fart types that have ammo (including basic)
     */
    getAvailableTypes(): FartType[] {
        return [...this.availableTypes];
    }

    /**
     * Get all ammo as array for UI display
     */
    getAllAmmo(): FartAmmo[] {
        const result: FartAmmo[] = [];

        for (const type of Object.values(FartType)) {
            result.push({
                type,
                count: this.getAmmo(type),
            });
        }

        return result;
    }

    /**
     * Get current selection info for UI
     */
    getSelectionInfo(): { type: FartType; icon: string; name: string; ammo: number } {
        const config = getFartConfig(this.selectedType);
        return {
            type: this.selectedType,
            icon: config.icon,
            name: config.name,
            ammo: this.getAmmo(this.selectedType),
        };
    }

    /**
     * Update the list of available fart types
     */
    private updateAvailableTypes(): void {
        this.availableTypes = [FartType.BASIC];

        for (const type of Object.values(FartType)) {
            if (type !== FartType.BASIC && this.getAmmo(type) > 0) {
                this.availableTypes.push(type);
            }
        }

        // Ensure selected type is still valid
        if (!this.availableTypes.includes(this.selectedType)) {
            this.selectedType = FartType.BASIC;
        }
    }

    /**
     * Reset inventory (for new round)
     */
    reset(): void {
        for (const type of Object.values(FartType)) {
            if (type !== FartType.BASIC) {
                this.ammo.set(type, 0);
            }
        }
        this.selectedType = FartType.BASIC;
        this.availableTypes = [FartType.BASIC];
    }

    /**
     * Serialize for network sync
     */
    serialize(): Record<string, number> {
        const data: Record<string, number> = {};
        for (const [type, count] of this.ammo.entries()) {
            data[type] = count;
        }
        return data;
    }

    /**
     * Deserialize from network sync
     */
    deserialize(data: Record<string, number>): void {
        const validTypes = Object.values(FartType) as string[];
        for (const [type, count] of Object.entries(data)) {
            if (validTypes.includes(type) && type !== FartType.BASIC) {
                this.ammo.set(type as FartType, count);
            }
        }
        this.updateAvailableTypes();
    }
}
